/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import type { FC } from 'react';
import React, { useCallback, Fragment, useContext, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useIntl } from 'react-intl-next';

import Link from '@atlaskit/link';
import type { AvatarProps } from '@atlaskit/avatar-group';
import AvatarGroup from '@atlaskit/avatar-group';
import AppIcon from '@atlaskit/icon/core/app';

import { fg } from '@confluence/feature-gating';
import {
	CUSTOM_CONTENT_SPACE_LIST_VIEW_EXPERIENCE,
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { CONTEXT_PATH } from '@confluence/named-routes';
import { ProfileAvatar, getAtlassianPeopleProfileUrl } from '@confluence/profile';
import { ObservableSpinner } from '@confluence/infinite-scroll';

import type {
	SpaceCustomContentListViewQuery_content_PaginatedContentListWithChild_nodes_Content as Nodes,
	SpaceCustomContentListViewQuery_content_PaginatedContentListWithChild_nodes_Content_history_History_contributors_Contributors_publishers_ContributorUsers as Publishers,
	SpaceCustomContentListViewQuery as SpaceCustomContentListViewQuery$data,
	SpaceCustomContentListViewQueryVariables as SpaceCustomContentListViewQuery$variables,
} from './__types__/SpaceCustomContentListViewQuery';
import { i18n } from './SpaceCustomContentListView-i18n';
import { CustomContentIcon, CustomContentTable } from './SpaceCustomContentListView-styles';
import { CustomContentErrorMessage } from './CustomContentErrorMessage';
import { CustomContentEmptyList } from './CustomContentEmptyList';
import { CustomContentLoading } from './CustomContentLoading';
import { formatDate } from './CustomContent-utils';

const PAGE_SIZE = 25;

type SpaceCustomContentListViewProps = {
	spaceKey: string;
	type: string;
	icon?: string;
};

const getAvatarData = (publishers: Publishers | null | undefined): AvatarProps[] => {
	if (!publishers?.users || !publishers?.userAccountIds) {
		return [];
	}
	return publishers.users.map((user, i) => {
		const name = user?.displayName || '';
		return {
			name,
			src: user?.profilePicture?.path,
			userId: publishers.userAccountIds[i],
			href: getAtlassianPeopleProfileUrl(publishers.userAccountIds[i], null, CONTEXT_PATH),
		};
	});
};

export const SpaceCustomContentListView: FC<SpaceCustomContentListViewProps> = ({
	spaceKey,
	type,
	icon,
}) => {
	const intl = useIntl();
	const experienceTracker = useContext(ExperienceTrackerContext);

	useEffect(() => {
		experienceTracker.start({
			name: CUSTOM_CONTENT_SPACE_LIST_VIEW_EXPERIENCE,
			timeout: 10000,
			attributes: {
				spaceKey,
				type,
			},
		});
	}, [spaceKey, type, experienceTracker]);

	const { error, loading, data, fetchMore } = useQuery<
		SpaceCustomContentListViewQuery$data,
		SpaceCustomContentListViewQuery$variables
	>(
		graphql`
			query SpaceCustomContentListViewQuery(
				$spaceKey: String!
				$type: String!
				$first: Int = 25
				$after: String
			) {
				content(spaceKey: $spaceKey, type: $type, first: $first, after: $after) {
					nodes {
						id
						title
						history {
							lastUpdated {
								when
							}
							contributors {
								publishers {
									userAccountIds
									users {
										displayName
										profilePicture {
											path
										}
									}
								}
							}
						}
						links {
							webui
						}
					}
					pageInfo {
						hasNextPage
						endCursor
					}
				}
			}
		`,
		{
			variables: {
				spaceKey,
				type,
				first: PAGE_SIZE,
			},
		},
	);

	const loadMoreData = useCallback(
		(after: string | null | undefined) => {
			if (!after) {
				return;
			}

			void fetchMore({
				variables: { after },
				updateQuery: (prev: SpaceCustomContentListViewQuery$data, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prev;
					}

					return {
						content: {
							nodes: [...(prev.content?.nodes || []), ...(fetchMoreResult.content?.nodes || [])],
							__typename: 'PaginatedContentListWithChild',
							pageInfo: fetchMoreResult?.content?.pageInfo || null,
						},
					};
				},
			});
		},
		[fetchMore],
	);

	const items = (data?.content?.nodes as Nodes[]) || [];
	const endCursor = data?.content?.pageInfo?.endCursor;
	const hasNextPage = data?.content?.pageInfo?.hasNextPage;

	if (loading) {
		return <CustomContentLoading />;
	}

	if (error) {
		experienceTracker.fail({
			name: CUSTOM_CONTENT_SPACE_LIST_VIEW_EXPERIENCE,
			error,
		});
		return <CustomContentErrorMessage error={error} />;
	}

	if (!loading && items.length === 0) {
		experienceTracker.succeed({
			name: CUSTOM_CONTENT_SPACE_LIST_VIEW_EXPERIENCE,
		});
		return <CustomContentEmptyList />;
	}

	return (
		<Fragment>
			<CustomContentTable>
				<thead>
					<tr>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<th className="iconCol" />
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<th className="nameCol">{intl.formatMessage(i18n.name)}</th>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<th className="contributorsCol">{intl.formatMessage(i18n.contributors)}</th>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<th className="lastModifiedCol">{intl.formatMessage(i18n.lastModified)}</th>
					</tr>
				</thead>
				<tbody>
					{items.map((item) => (
						<tr key={item.id}>
							<td>
								<CustomContentIcon
									data-vc="space-custom-content-list-view-icon"
									width="16"
									height="16"
									src={icon}
									fallback={AppIcon}
									alt=""
								/>
							</td>
							<td>
								{fg('dst-a11y__replace-anchor-with-link__ecosystem') ? (
									<Link href={`${CONTEXT_PATH}${item.links?.webui}`}>{item.title}</Link>
								) : (
									// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
									<a href={`${CONTEXT_PATH}${item.links?.webui}`}>{item.title}</a>
								)}
							</td>
							<td>
								<AvatarGroup
									size="small"
									isTooltipDisabled
									avatar={ProfileAvatar}
									data={getAvatarData(item.history?.contributors?.publishers)}
								/>
							</td>
							<td>{formatDate(item.history?.lastUpdated?.when)}</td>
						</tr>
					))}
				</tbody>
			</CustomContentTable>
			<ExperienceSuccess name={CUSTOM_CONTENT_SPACE_LIST_VIEW_EXPERIENCE} />
			{hasNextPage && (
				<ObservableSpinner
					cursor={endCursor}
					testId="custom-content-space-list-view-paginating-spinner"
					onScrollTo={() => loadMoreData(endCursor)}
					observerOptions={{
						root: null,
						rootMargin: '0px',
						threshold: 0.05,
					}}
				/>
			)}
		</Fragment>
	);
};
